import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useService, useSEO } from '../../hooks';
import { bookingAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { format, addDays } from 'date-fns';
import toast from 'react-hot-toast';
import resolveImage from '../../utils/imageUrl';
import {
  FiStar, FiClock, FiShield, FiCheck,
  FiArrowLeft, FiArrowRight, FiMapPin,
} from 'react-icons/fi';
import './BookingFlow.css';

const TIME_SLOTS = [
  '8:00 AM - 10:00 AM',
  '10:00 AM - 12:00 PM',
  '12:00 PM - 2:00 PM',
  '2:00 PM - 4:00 PM',
  '4:00 PM - 6:00 PM',
  '6:00 PM - 8:00 PM',
];

const generateDates = () => Array.from({ length: 7 }, (_, i) => addDays(new Date(), i + 1));
const STEPS = ['Service', 'Schedule', 'Address', 'Confirm'];

const STORAGE_KEY = 'booking_draft';

const BookingFlow = () => {
  const { slug } = useParams();
  const { service, loading: serviceLoading } = useService(slug);
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  // ── Dynamic SEO per service ───────────────────────────
  useSEO({
    title: service
      ? `Book ${service.name} in Kolkata`
      : 'Book a Service in Kolkata',
    description: service
      ? `${service.shortDescription || `Book professional ${service.name} in Kolkata`}. Verified experts, pay after service. From ₹${service.basePrice}.`
      : 'Book a professional home service in Kolkata on ExpertAtDoor. Verified experts, zero upfront payment.',
    canonical: `/services/${slug}`,
    noindex: true,   // booking funnel — keep out of index
  });

  const [step,         setStep]         = useState(0);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState('');
  const [address, setAddress] = useState({
    line1: '', line2: '',
    city: 'Kolkata', state: 'West Bengal',
    pincode: '', landmark: '', email: '',
  });
  const [notes,    setNotes]    = useState('');
  const [booking,  setBooking]  = useState(false);
  const [errors,   setErrors]   = useState({});

  const dates = generateDates();

  // ── When user comes back from login, restore draft and auto-confirm ──
  useEffect(() => {
    if (!isAuthenticated || authLoading) return;
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    try {
      const draft = JSON.parse(raw);
      if (draft.slug !== slug) return;          // different service
      sessionStorage.removeItem(STORAGE_KEY);   // consume draft
      setSelectedDate(new Date(draft.selectedDate));
      setSelectedSlot(draft.selectedSlot);
      setAddress(draft.address);  // draft.address now includes email
      setNotes(draft.notes || '');
      setStep(3);                               // jump straight to confirm
      toast.success('Welcome back! Review your booking below.');
    } catch { sessionStorage.removeItem(STORAGE_KEY); }
  }, [isAuthenticated, authLoading, slug]);

  // ── Pre-fill saved address when user is logged in ────────
  useEffect(() => {
    if (user?.addresses?.length > 0 && !address.line1) {
      const a = user.addresses[0];
      setAddress({
        line1:    a.line1    || '',
        line2:    a.line2    || '',
        city:     a.city     || 'Kolkata',
        state:    a.state    || 'West Bengal',
        pincode:  a.pincode  || '',
        landmark: a.landmark || '',
        email:    user.email || '',
      });
    } else if (user?.email && !address.email) {
      setAddress(prev => ({ ...prev, email: user.email }));
    }
  }, [user]);

  const validateAddress = () => {
    const e = {};
    if (!address.line1.trim())             e.line1   = 'Address is required';
    if (!address.pincode.match(/^\d{6}$/)) e.pincode = 'Valid 6-digit pincode required';
    if (!address.email.trim())             e.email   = 'Email address is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(address.email.trim()))
                                           e.email   = 'Enter a valid email address';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleContinue = () => {
    if (step === 1 && (!selectedDate || !selectedSlot)) return;
    if (step === 2) {
      if (!validateAddress()) return;

      // If not logged in — save draft to sessionStorage, redirect to login
      if (!authLoading && !isAuthenticated) {
        const draft = {
          slug,
          selectedDate: selectedDate.toISOString(),
          selectedSlot,
          address,
          notes,
        };
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(draft));
        toast('Please sign in to confirm your booking', { icon: '🔒' });
        navigate('/login', { state: { from: { pathname: `/services/${slug}` } } });
        return;
      }
    }
    setStep(s => s + 1);
  };

  const handleBook = async () => {
    if (!isAuthenticated) {
      toast('Please sign in to book', { icon: '🔒' });
      navigate('/login', { state: { from: { pathname: `/services/${slug}` } } });
      return;
    }

    setBooking(true);
    try {
      const payload = {
        serviceId:         service._id,
        scheduledDate:     selectedDate.toISOString(),
        scheduledTimeSlot: selectedSlot,
        address: {
          line1:    address.line1.trim(),
          line2:    (address.line2    || '').trim(),
          city:     (address.city     || 'kolkata').toLowerCase().trim(),
          state:    (address.state    || 'West Bengal').trim(),
          pincode:  address.pincode.trim(),
          landmark: (address.landmark || '').trim(),
        },
        contactEmail: address.email.trim(),
        paymentMode: 'cod',
        notes: (notes || '').trim(),
      };

      const { data } = await bookingAPI.create(payload);
      sessionStorage.removeItem(STORAGE_KEY);
      toast.success('Booking confirmed! 🎉');
      navigate(`/booking/${data.data.bookingId}`);
    } catch (err) {
      const status = err.response?.status;
      const msg    = err.response?.data?.message || err.message || 'Booking failed. Please try again.';
      toast.error(msg);
      if (status === 401) {
        navigate('/login', { state: { from: { pathname: `/services/${slug}` } } });
      }
    } finally {
      setBooking(false);
    }
  };

  if (serviceLoading) return (
    <div className="booking-flow-page">
      <div className="container">
        <div className="skeleton" style={{ height: 500, borderRadius: 'var(--radius-lg)' }} />
      </div>
    </div>
  );

  if (!service) return null;

  const savedAddresses = user?.addresses || [];

  return (
    <div className="booking-flow-page">
      <div className="container">

        {/* Progress */}
        <div className="booking-progress">
          {STEPS.map((s, i) => (
            <React.Fragment key={s}>
              <div className={`progress-step ${i < step ? 'done' : ''} ${i === step ? 'active' : ''}`}>
                <div className="progress-dot">
                  {i < step ? <FiCheck size={11} /> : i + 1}
                </div>
                <span>{s}</span>
              </div>
              {i < STEPS.length - 1 && (
                <div className={`progress-line ${i < step ? 'done' : ''}`} />
              )}
            </React.Fragment>
          ))}
        </div>

        <div className="booking-flow-grid">

          {/* ── Main panel ───────────────────────────── */}
          <div>

            {/* Step 0: Service info */}
            {step === 0 && (
              <div className="flow-card">
                <div className="service-hero-img">
                  {service.image
                    ? <img src={resolveImage(service.image)} alt={service.name} />
                    : <div className="service-img-placeholder">🧹</div>}
                </div>
                <div className="flow-card-body">
                  <div className="flow-card-meta">
                    <span className="service-category">{service.categoryId?.name}</span>
                    {service.averageRating > 0 && (
                      <span className="service-rating-badge">
                        <FiStar size={12} />
                        {service.averageRating} ({service.totalReviews} reviews)
                      </span>
                    )}
                  </div>
                  <h2 style={{ marginBottom: 'var(--sp-3)' }}>{service.name}</h2>
                  <p style={{ marginBottom: 'var(--sp-5)' }}>{service.description}</p>

                  <div className="service-chips">
                    {service.duration && (
                      <span className="chip"><FiClock size={12} /> ~{service.duration} min</span>
                    )}
                    {service.warranty && (
                      <span className="chip"><FiShield size={12} /> {service.warranty}</span>
                    )}
                  </div>

                  {service.faqs?.length > 0 && (
                    <div className="faqs">
                      <h4 style={{ marginBottom: 'var(--sp-3)' }}>Frequently asked</h4>
                      {service.faqs.map((faq, i) => (
                        <details key={i} className="faq-item">
                          <summary>{faq.question}</summary>
                          <p>{faq.answer}</p>
                        </details>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Step 1: Schedule */}
            {step === 1 && (
              <div className="flow-card flow-card-body">
                <h3 style={{ marginBottom: 'var(--sp-6)' }}>Pick a Date &amp; Time</h3>

                <span className="flow-section-label">Select Date</span>
                <div className="date-picker">
                  {dates.map((date) => {
                    const isSelected = selectedDate &&
                      format(date, 'yyyy-MM-dd') === format(selectedDate, 'yyyy-MM-dd');
                    return (
                      <button
                        key={date.toISOString()}
                        className={`date-btn ${isSelected ? 'active' : ''}`}
                        onClick={() => setSelectedDate(date)}
                      >
                        <span className="date-day">{format(date, 'EEE')}</span>
                        <span className="date-num">{format(date, 'd')}</span>
                        <span className="date-mon">{format(date, 'MMM')}</span>
                      </button>
                    );
                  })}
                </div>

                {selectedDate && (
                  <>
                    <span className="flow-section-label" style={{ marginTop: 'var(--sp-6)', display: 'block' }}>
                      Select Time Slot
                    </span>
                    <div className="slot-grid">
                      {TIME_SLOTS.map((slot) => (
                        <button
                          key={slot}
                          className={`slot-btn ${selectedSlot === slot ? 'active' : ''}`}
                          onClick={() => setSelectedSlot(slot)}
                        >
                          {slot}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Step 2: Address */}
            {step === 2 && (
              <div className="flow-card flow-card-body">
                <h3 style={{ marginBottom: 'var(--sp-5)' }}>Service Address</h3>

                {savedAddresses.length > 0 && (
                  <>
                    <span className="flow-section-label">Saved Addresses</span>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)', marginBottom: 'var(--sp-5)' }}>
                      {savedAddresses.map((addr) => (
                        <button
                          key={addr._id}
                          className={`saved-addr-btn ${address.line1 === addr.line1 ? 'active' : ''}`}
                          onClick={() => setAddress({
                            line1:    addr.line1    || '',
                            line2:    addr.line2    || '',
                            city:     addr.city     || 'Kolkata',
                            state:    addr.state    || 'West Bengal',
                            pincode:  addr.pincode  || '',
                            landmark: addr.landmark || '',
                            email:    address.email || user?.email || '',
                          })}
                        >
                          <FiMapPin size={14} />
                          <div>
                            <strong>{addr.label || 'Home'}</strong>
                            <p>{addr.line1}, {addr.city} — {addr.pincode}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                    <span className="flow-section-label">Or enter a new address</span>
                  </>
                )}

                <div className="address-form">
                  <div className="form-group">
                    <label className="form-label">Email Address *</label>
                    <input
                      className={`form-input ${errors.email ? 'error' : ''}`}
                      type="email"
                      placeholder="you@example.com"
                      value={address.email}
                      onChange={(e) => setAddress({ ...address, email: e.target.value })}
                    />
                    {errors.email && <span className="field-error">{errors.email}</span>}
                    <span style={{ fontSize: '0.75rem', color: 'var(--brand-muted)', marginTop: 4, display: 'block' }}>
                      Booking confirmation will be sent to this email
                    </span>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Address Line 1 *</label>
                    <input
                      className={`form-input ${errors.line1 ? 'error' : ''}`}
                      type="text"
                      placeholder="Flat / house no., street"
                      value={address.line1}
                      onChange={(e) => setAddress({ ...address, line1: e.target.value })}
                    />
                    {errors.line1 && <span className="field-error">{errors.line1}</span>}
                  </div>

                  <div className="form-group">
                    <label className="form-label">Address Line 2</label>
                    <input
                      className="form-input"
                      type="text"
                      placeholder="Area, colony (optional)"
                      value={address.line2}
                      onChange={(e) => setAddress({ ...address, line2: e.target.value })}
                    />
                  </div>

                  <div className="address-row">
                    <div className="form-group">
                      <label className="form-label">City</label>
                      <input
                        className="form-input"
                        type="text"
                        value={address.city}
                        onChange={(e) => setAddress({ ...address, city: e.target.value })}
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Pincode *</label>
                      <input
                        className={`form-input ${errors.pincode ? 'error' : ''}`}
                        type="text"
                        inputMode="numeric"
                        maxLength={6}
                        placeholder="700001"
                        value={address.pincode}
                        onChange={(e) => setAddress({ ...address, pincode: e.target.value.replace(/\D/g, '') })}
                      />
                      {errors.pincode && <span className="field-error">{errors.pincode}</span>}
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Landmark (optional)</label>
                    <input
                      className="form-input"
                      type="text"
                      placeholder="Near park, school, etc."
                      value={address.landmark}
                      onChange={(e) => setAddress({ ...address, landmark: e.target.value })}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Notes for Cleaner</label>
                    <textarea
                      className="form-input"
                      rows={2}
                      placeholder="Any special instructions..."
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      style={{ resize: 'vertical' }}
                    />
                  </div>
                </div>

                {/* Sign-in nudge for guest users */}
                {!isAuthenticated && (
                  <div className="auth-nudge">
                    <span>🔒</span>
                    <p>
                      You'll be asked to <Link to="/login" style={{ color: 'var(--status-info)', fontWeight: 700 }}>sign in</Link> before
                      confirming — your details will be saved automatically.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Step 3: Confirm */}
            {step === 3 && (
              <div className="flow-card flow-card-body">
                <h3 style={{ marginBottom: 'var(--sp-6)' }}>Review &amp; Confirm</h3>

                {[
                  { label: 'Service',  value: service.name },
                  { label: 'Date',     value: selectedDate ? format(selectedDate, 'EEEE, dd MMMM yyyy') : '—' },
                  { label: 'Time',     value: selectedSlot || '—' },
                  { label: 'Address',  value: `${address.line1}, ${address.city} — ${address.pincode}` },
                  { label: 'Email',    value: address.email || '—' },
                  ...(notes ? [{ label: 'Notes', value: notes }] : []),
                ].map(({ label, value }) => (
                  <div key={label} className="confirm-row">
                    <span className="confirm-key">{label}</span>
                    <span className="confirm-val">{value}</span>
                  </div>
                ))}

                <div className="pay-after-notice">
                  <span className="pay-after-icon">💳</span>
                  <div>
                    <p className="pay-after-title">Pay after service</p>
                    <p className="pay-after-desc">
                      Pay in cash or UPI directly to the expert once the job is completed
                      to your satisfaction. Zero upfront payment required.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Navigation */}
            <div className="flow-nav">
              {step > 0 && (
                <button className="btn btn-ghost" onClick={() => setStep(s => s - 1)}>
                  <FiArrowLeft size={15} /> Back
                </button>
              )}
              {step < 3 ? (
                <button
                  className="btn btn-primary"
                  style={{ marginLeft: 'auto' }}
                  disabled={step === 1 && (!selectedDate || !selectedSlot)}
                  onClick={handleContinue}
                >
                  {step === 2 && !isAuthenticated ? 'Sign in & Continue' : 'Continue'}
                  <FiArrowRight size={15} />
                </button>
              ) : (
                <button
                  className="btn btn-primary"
                  style={{ marginLeft: 'auto' }}
                  disabled={booking}
                  onClick={handleBook}
                >
                  {booking ? 'Booking...' : `Confirm Booking · ₹${service.basePrice}`}
                </button>
              )}
            </div>
          </div>

          {/* ── Summary sidebar ──────────────────────── */}
          <div className="booking-summary-card">
            <h4>Order Summary</h4>
            <div className="summary-service">
              <div className="summary-img">
                {service.image
                  ? <img src={resolveImage(service.image)} alt={service.name} />
                  : '🧹'}
              </div>
              <div>
                <p className="summary-name">{service.name}</p>
                <p className="summary-category">{service.categoryId?.name}</p>
              </div>
            </div>

            <div className="summary-rows">
              <div className="summary-row"><span>Base price</span><span>₹{service.basePrice}</span></div>
              {selectedDate && (
                <div className="summary-row">
                  <span>Date</span><span>{format(selectedDate, 'dd MMM')}</span>
                </div>
              )}
              {selectedSlot && (
                <div className="summary-row">
                  <span>Time</span>
                  <span style={{ fontSize: '0.78rem' }}>{selectedSlot}</span>
                </div>
              )}
              <div className="summary-total">
                <span>Total</span><span>₹{service.basePrice}</span>
              </div>
            </div>

            <div className="summary-trust">
              <span><FiCheck size={12} /> Verified cleaner</span>
              <span><FiShield size={12} /> Pay after service only</span>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default BookingFlow;
